/**
 * Availability API Client
 * Fetches factories, variants, and availability records from Strapi
 */

import { getHttpClient } from '@spcc/core'
import type { FactorySummary, VariantSummary, AvailabilityRecord } from '../../domain/types'
import {
  AVAILABILITY_ENDPOINTS,
  type FactoryDto,
  type VariantDto,
  type AvailabilityRecordDto,
  type CreateAvailabilityRequest,
} from '../../api/api-types'
import { transformFactoryDtoToSummary, transformVariantDtoToSummary } from '../../api/transformers'

// ============================================================================
// Helper Functions
// ============================================================================

function buildQueryString(params: Record<string, string | number | boolean>): string {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    searchParams.append(key, String(value))
  })
  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}

// ============================================================================
// API Client
// ============================================================================

export const availabilityApi = {
  /**
   * Get all active factories
   */
  async getFactories(): Promise<FactorySummary[]> {
    const http = getHttpClient()
    const queryString = buildQueryString({
      'filters[factoryStatus][$eq]': 'active',
      'filters[deletedAt][$null]': 'true',
      'sort': 'name:asc',
      'pagination[pageSize]': '100',
    })

    const response = await http.get<{ data: FactoryDto[] }>(
      `${AVAILABILITY_ENDPOINTS.FACTORIES}${queryString}`
    )

    return response.data.data.map(transformFactoryDtoToSummary)
  },

  /**
   * Get all active variants with their SKU info
   */
  async getVariants(): Promise<VariantSummary[]> {
    const http = getHttpClient()
    const queryString = buildQueryString({
      'filters[isActive][$eq]': 'true',
      'filters[deletedAt][$null]': 'true',
      'filters[sku][deletedAt][$null]': 'true',
      'populate[sku]': 'true',
      'sort': 'variantCode:asc',
      'pagination[pageSize]': '100',
    })

    const response = await http.get<{ data: VariantDto[] }>(
      `${AVAILABILITY_ENDPOINTS.VARIANTS}${queryString}`
    )

    return response.data.data
      .filter((variant) => Boolean(variant.sku?.documentId))
      .map(transformVariantDtoToSummary)
  },

  /**
   * Get all availability records
   */
  async getAvailabilityRecords(): Promise<AvailabilityRecord[]> {
    const http = getHttpClient()
    const queryString = buildQueryString({
      // Use simple populate to guarantee documentId is included in Strapi 5
      'populate[factory]': 'true',
      'populate[variant]': 'true',
      'pagination[pageSize]': '1000',
    })

    const response = await http.get<{ data: AvailabilityRecordDto[] }>(
      `${AVAILABILITY_ENDPOINTS.AVAILABILITY}${queryString}`
    )

    return response.data.data.map((dto) => ({
      id: String(dto.id),
      documentId: dto.documentId,
      factoryDocumentId: dto.factory?.documentId || '',
      variantDocumentId: dto.variant?.documentId || '',
      isAvailable: dto.isAvailable ?? true,
    }))
  },

  /**
   * Get full availability matrix with all data
   */
  async getAvailabilityMatrix(): Promise<{
    factories: FactorySummary[]
    variants: VariantSummary[]
    availability: Record<string, Record<string, boolean>>
    records: AvailabilityRecord[]
  }> {
    // Fetch all data in parallel
    const [factories, variants, records] = await Promise.all([
      availabilityApi.getFactories(),
      availabilityApi.getVariants(),
      availabilityApi.getAvailabilityRecords(),
    ])

    // Build availability matrix from records
    // Default all to true (available), then overlay with actual records
    const availability: Record<string, Record<string, boolean>> = {}
    factories.forEach((factory) => {
      availability[factory.documentId] = {}
      variants.forEach((variant) => {
        availability[factory.documentId][variant.documentId] = true
      })
    })

    // Overlay with actual availability records
    records.forEach((record) => {
      if (record.factoryDocumentId && record.variantDocumentId) {
        if (availability[record.factoryDocumentId]) {
          availability[record.factoryDocumentId][record.variantDocumentId] = record.isAvailable
        }
      }
    })

    return { factories, variants, availability, records }
  },

  /**
   * Find availability record by factory and variant
   */
  async findAvailabilityRecord(
    factoryDocumentId: string,
    variantDocumentId: string
  ): Promise<AvailabilityRecord | null> {
    const http = getHttpClient()
    const queryString = buildQueryString({
      'filters[factory][documentId][$eq]': factoryDocumentId,
      'filters[variant][documentId][$eq]': variantDocumentId,
      // Use simple populate to guarantee documentId is returned
      'populate[factory]': 'true',
      'populate[variant]': 'true',
    })

    const response = await http.get<{ data: AvailabilityRecordDto[] }>(
      `${AVAILABILITY_ENDPOINTS.AVAILABILITY}${queryString}`
    )

    if (response.data.data.length === 0) {
      return null
    }

    const dto = response.data.data[0]
    return {
      id: String(dto.id),
      documentId: dto.documentId,
      factoryDocumentId: dto.factory?.documentId || '',
      variantDocumentId: dto.variant?.documentId || '',
      isAvailable: dto.isAvailable ?? true,
    }
  },

  /**
   * Create a new availability record
   */
  async createAvailability(request: CreateAvailabilityRequest): Promise<AvailabilityRecord> {
    const http = getHttpClient()
    // Strapi 5 REST API requires connect syntax for relation fields
    const response = await http.post<{ data: AvailabilityRecordDto }>(
      AVAILABILITY_ENDPOINTS.AVAILABILITY,
      {
        data: {
          factory: { connect: [{ documentId: request.factory }] },
          variant: { connect: [{ documentId: request.variant }] },
          isAvailable: request.isAvailable,
        },
      }
    )

    const dto = response.data.data
    return {
      id: String(dto.id),
      documentId: dto.documentId,
      factoryDocumentId: request.factory,
      variantDocumentId: request.variant,
      isAvailable: dto.isAvailable ?? true,
    }
  },

  /**
   * Update an existing availability record
   */
  async updateAvailability(documentId: string, isAvailable: boolean): Promise<boolean> {
    const http = getHttpClient()
    try {
      await http.put(AVAILABILITY_ENDPOINTS.AVAILABILITY_BY_ID(documentId), {
        data: { isAvailable },
      })
      return true
    } catch {
      return false
    }
  },

  /**
   * Upsert availability (create or update)
   */
  async upsertAvailability(
    factoryDocumentId: string,
    variantDocumentId: string,
    isAvailable: boolean
  ): Promise<boolean> {
    // First, check if record exists
    const existing = await availabilityApi.findAvailabilityRecord(
      factoryDocumentId,
      variantDocumentId
    )

    if (existing) {
      // Update existing record
      return availabilityApi.updateAvailability(existing.documentId, isAvailable)
    } else {
      // Create new record
      try {
        await availabilityApi.createAvailability({
          factory: factoryDocumentId,
          variant: variantDocumentId,
          isAvailable,
        })
        return true
      } catch {
        return false
      }
    }
  },

  /**
   * Bulk update availability records
   * If existingDocumentId is provided, directly updates that record instead of doing a lookup.
   */
  async bulkUpdateAvailability(
    changes: Array<{
      factoryDocumentId: string
      variantDocumentId: string
      isAvailable: boolean
      existingDocumentId?: string | null
    }>
  ): Promise<{ success: number; failed: number }> {
    let success = 0
    let failed = 0

    // Process changes sequentially to avoid overwhelming the API
    for (const change of changes) {
      let result: boolean

      if (change.existingDocumentId) {
        // Directly update the known record — no API lookup needed
        result = await availabilityApi.updateAvailability(change.existingDocumentId, change.isAvailable)
      } else {
        // No existing record known: create a new one
        result = await availabilityApi.upsertAvailability(
          change.factoryDocumentId,
          change.variantDocumentId,
          change.isAvailable
        )
      }

      if (result) {
        success++
      } else {
        failed++
      }
    }

    return { success, failed }
  },
}
